import { useMatter } from '@/hooks/useMatter';
import { ArtifactType } from '@/services/types/artifact-digest-types';

import { formatArtifactTypeForDisplay } from '../utils/artifactFormatters';
import { ArtifactSearchBar } from './ArtifactSearchBar';
import { ArtifactStats } from './ArtifactStats';

interface ArtifactStatsData {
  total: number;
  withDigest: number;
  typeStats: Record<ArtifactType, number>;
}

interface ArtifactListHeaderProps {
  totalCount: number;
  filteredCount: number;
  sortOrder: 'asc' | 'desc';
  onToggleSort: () => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  typeFilter: ArtifactType | 'unprocessed' | 'all';
  onTypeFilterChange: (type: ArtifactType | 'unprocessed' | 'all') => void;
  availableTypes: ArtifactType[];
  stats?: ArtifactStatsData | null;
  onClearFilters?: () => void;
  isSelectionMode?: boolean;
  onToggleSelectionMode?: () => void;
  selectedCount?: number;
}

export const ArtifactListHeader = ({
  totalCount,
  filteredCount,
  sortOrder,
  onToggleSort,
  searchQuery,
  onSearchChange,
  typeFilter,
  onTypeFilterChange,
  availableTypes,
  stats,
  onClearFilters,
  isSelectionMode = false,
  onToggleSelectionMode,
  selectedCount = 0,
}: ArtifactListHeaderProps) => {
  const { matterId } = useMatter();
  const hasActiveFilters = searchQuery || typeFilter !== 'all';
  const isFiltered = filteredCount !== totalCount;
  const showingSelection = isSelectionMode && selectedCount > 0;

  return (
    <div className="border-b bg-background px-6 py-6">
      <div className="max-w-4xl mx-auto space-y-4">
        {/* Title Row */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-base font-medium text-foreground">
              {showingSelection ? `${selectedCount} selected` : 'Artifacts'}
            </h1>
            <p className="text-xs text-muted-foreground/60 mt-0.5">
              {isFiltered
                ? `${filteredCount} of ${totalCount}`
                : `${totalCount} total`}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {onToggleSelectionMode && (
              <button
                type="button"
                onClick={onToggleSelectionMode}
                className={`px-3 py-2 text-sm rounded-lg border font-medium transition-all ${
                  isSelectionMode
                    ? 'bg-primary/10 text-primary border-primary/30 hover:bg-primary/15 shadow-sm'
                    : 'bg-background border-border hover:bg-muted hover:border-border/80 shadow-sm'
                }`}
              >
                {isSelectionMode ? 'Exit' : 'Select'}
              </button>
            )}
            <button
              type="button"
              onClick={onToggleSort}
              className="px-3 py-2 text-sm rounded-lg border bg-background border-border hover:bg-muted hover:border-border/80 font-medium transition-all shadow-sm"
              aria-label={`Sort by date ${sortOrder === 'desc' ? 'ascending' : 'descending'}`}
            >
              {sortOrder === 'desc' ? '↓' : '↑'}
            </button>
          </div>
        </div>

        {/* Prominent Search */}
        <div className="py-1">
          <ArtifactSearchBar
            searchQuery={searchQuery}
            onSearchChange={onSearchChange}
            typeFilter={typeFilter}
            onTypeFilterChange={onTypeFilterChange}
            availableTypes={availableTypes}
          />
        </div>

        {/* Stats */}
        {stats && (
          <ArtifactStats
            stats={stats}
            onTypeFilter={onTypeFilterChange}
            activeFilter={typeFilter}
            matterId={matterId}
          />
        )}

        {/* Active Filters */}
        {hasActiveFilters && (
          <div className="flex items-center justify-center gap-2 text-xs">
            <span className="text-muted-foreground/50">Filtered:</span>
            {searchQuery && (
              <span className="bg-muted/60 text-muted-foreground px-2 py-0.5 rounded text-xs">
                &quot;{searchQuery}&quot;
              </span>
            )}
            {typeFilter !== 'all' && (
              <span className="bg-muted/60 text-muted-foreground px-2 py-0.5 rounded text-xs">
                {formatArtifactTypeForDisplay(typeFilter)}
              </span>
            )}
            {onClearFilters && (
              <button
                type="button"
                onClick={onClearFilters}
                className="text-muted-foreground/50 hover:text-foreground underline underline-offset-2 text-xs ml-1"
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
